import { Box, Button, Dialog, IconButton, Stack, Switch, Typography } from '@mui/material';
import { DEFAULT_DATE_FORMAT_FNS, FilterKeys } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupSelectMultiple,
  GenderSelectMultiple,
  GroupSelectMultiple,
  NationalitySelectMultiple,
  PropertyTypeSelectMultiple,
  SchoolPeriodsRangeSelect,
  SubjectSelectMultiple,
  toggleMultipleValueArrayProperty,
} from '@schooly/components/filters';
import { Genders, Nationalities, SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { CrossIcon, ModalContent, ModalFooter, ModalHeader } from '@schooly/style';
import { format } from 'date-fns';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../hooks/useSchool';
import useSchoolYears from '../../../hooks/useSchoolYears';

export type AddStudentsFilters = {
  [FilterKeys.Status]?: string[];
  [FilterKeys.AgeGroup]?: string[];
  [FilterKeys.House]?: string[];
  [FilterKeys.Group]?: string[];
  [FilterKeys.SingleDate]?: string[];
  [FilterKeys.Date]?: string[];
  [FilterKeys.Subject]?: string[];
  [FilterKeys.Nationality]?: Nationalities[];
  [FilterKeys.Gender]?: Genders[];
};

type CommonFilterKey = Exclude<AddStudentsFiltersKey, FilterKeys.Gender | FilterKeys.Nationality>;

export type AddStudentsFiltersKey =
  | FilterKeys.Date
  | FilterKeys.Status
  | FilterKeys.AgeGroup
  | FilterKeys.House
  | FilterKeys.Group
  | FilterKeys.Subject
  | FilterKeys.Nationality
  | FilterKeys.Gender;

export type AddStudentsFiltersPopupProps = {
  excludeGroups?: boolean;
  filtersAvailable: AddStudentsFiltersKey[];
  filters: AddStudentsFilters;
  defaultFilters: AddStudentsFilters;
  onClose: () => void;
  onSetFilters: (v: AddStudentsFilters, excludeGroups?: boolean) => void;
};

export const ALL_FILTERS: AddStudentsFiltersKey[] = [
  FilterKeys.Date,
  FilterKeys.Status,
  FilterKeys.AgeGroup,
  FilterKeys.House,
  FilterKeys.Group,
  FilterKeys.Subject,
  FilterKeys.Gender,
  FilterKeys.Nationality,
];

export const AddStudentsFiltersPopup: FC<AddStudentsFiltersPopupProps> = ({
  excludeGroups: initialExcludeGroups,
  filtersAvailable,
  onClose,
  onSetFilters,
  filters: initialFilters,
  defaultFilters,
}) => {
  const { formatMessage } = useIntl();
  const { defaultValidity } = useSchoolYears();
  const { schoolId } = useSchool();
  const { permissions } = useAuth();
  const isGroupViewer = permissions.includes('group_viewer');

  const [filters, setFilters] = useState<AddStudentsFilters>(initialFilters);
  const [excludeGroups, setExcludeGroups] = useState<boolean>(initialExcludeGroups ?? false);

  const handleApply = useCallback(() => {
    onSetFilters(filters, initialExcludeGroups !== undefined ? excludeGroups : undefined);
    onClose();
  }, [onSetFilters, filters, initialExcludeGroups, excludeGroups, onClose]);

  const handleClear = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  const {
    onSetDate,
    onSelectStudentStatusId,
    onSelectAgeGroup,
    onSelectGroupId,
    onSelectHouseId,
    onClearStudentStatus,
    onClearAgeGroup,
    onClearGroup,
    onClearHouse,
    onClearDate,
    onSelectSubject,
    onSelectNationality,
    onSelectGender,
    onClearSubject,
    onClearNationality,
    onClearGender,
  } = useMemo(() => {
    const updateFilter = (key: CommonFilterKey) => (id: string) => {
      setFilters((v) => ({
        ...v,
        [key]:
          v[key] && v[key]?.includes(id)
            ? v[key]?.filter((ct) => ct !== id)
            : [...(v[key] || []), id],
      }));
    };
    const updateFilterMultipleValue = (key: CommonFilterKey) => (ids: string[]) => {
      setFilters((v) => ({ ...v, [key]: toggleMultipleValueArrayProperty(v[key], ids) }));
    };
    const clearFilter = (key: AddStudentsFiltersKey) => () => {
      setFilters((v) => ({
        ...v,
        [key]: [],
      }));
    };

    const onSelectGender = (v: Genders) => {
      setFilters((criteria) => ({
        ...criteria,
        [FilterKeys.Gender]: criteria['gender']?.includes(v)
          ? criteria['gender']?.filter((ct) => ct !== v)
          : [...(criteria['gender'] || []), v],
      }));
    };

    const onSelectNationality = (v: Nationalities) => {
      setFilters((criteria) => ({
        ...criteria,
        [FilterKeys.Nationality]: criteria['nationality']?.includes(v)
          ? criteria['nationality']?.filter((ct) => ct !== v)
          : [...(criteria['nationality'] || []), v],
      }));
    };

    return {
      onSetDate: ([fromDate, toDate]: [Date, Date]) =>
        setFilters((s) => ({
          ...s,
          [FilterKeys.Date]: [
            format(fromDate, DEFAULT_DATE_FORMAT_FNS),
            format(toDate, DEFAULT_DATE_FORMAT_FNS),
          ],
        })),
      onSelectGender,
      onSelectNationality,
      onSelectSubject: updateFilter(FilterKeys.Subject),
      onSelectStudentStatusId: updateFilter(FilterKeys.Status),
      onSelectAgeGroup: updateFilterMultipleValue(FilterKeys.AgeGroup),
      onSelectHouseId: updateFilter(FilterKeys.House),
      onSelectGroupId: updateFilter(FilterKeys.Group),
      onClearStudentStatus: clearFilter(FilterKeys.Status),
      onClearAgeGroup: clearFilter(FilterKeys.AgeGroup),
      onClearHouse: clearFilter(FilterKeys.House),
      onClearGroup: clearFilter(FilterKeys.Group),
      onClearDate: clearFilter(FilterKeys.Date),
      onClearSubject: clearFilter(FilterKeys.Subject),
      onClearNationality: clearFilter(FilterKeys.Nationality),
      onClearGender: clearFilter(FilterKeys.Gender),
    };
  }, []);

  const groupFilters = useMemo(() => {
    if (!filters.date) return undefined;

    return { [FilterKeys.IntersectDate]: filters.date };
  }, [filters.date]);

  if (!schoolId) return null;
  return (
    <Dialog
      fullWidth
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        zIndex: theme.zIndex.modal + 1,

        '& .MuiDialog-paperFullWidth': {
          width: 600,
          height: 'unset',
        },
      })}
      open
      onClose={onClose}
    >
      <ModalHeader active title={formatMessage({ id: 'reports-Filter-Title' })}>
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      <ModalContent active>
        {filtersAvailable.includes(FilterKeys.Date) && (
          <FilterRow label={formatMessage({ id: 'schoolProperty-Date' })}>
            <SchoolPeriodsRangeSelect
              borderOnHover
              schoolId={schoolId}
              defaultSchoolYear={defaultValidity}
              hasManagePermission={permissions.includes('school_admin')}
              onSetDate={onSetDate}
              onClear={onClearDate}
              date={filters[FilterKeys.Date] || []}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.Status) && (
          <FilterRow label={formatMessage({ id: 'schoolProperty-Status' })}>
            <PropertyTypeSelectMultiple
              borderOnHover
              allowArchived
              schoolId={schoolId}
              onClear={onClearStudentStatus}
              optionsTitle={formatMessage({ id: 'schoolProperty-Status-plural' })}
              userRole={SchoolUserRole.Student}
              propertyType={SchoolPropertyType.Status}
              selectedIds={filters.status || []}
              onSelectId={onSelectStudentStatusId}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.AgeGroup) && (
          <FilterRow label={formatMessage({ id: 'schoolProperty-AgeGroup' })}>
            <AgeGroupSelectMultiple
              borderOnHover
              allowArchived
              schoolId={schoolId}
              onClear={onClearAgeGroup}
              optionsTitle={formatMessage({ id: 'schoolProperty-AgeGroup-plural' })}
              userRole={SchoolUserRole.Student}
              selectedIds={filters.age_group || []}
              onSelect={onSelectAgeGroup}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.House) && (
          <FilterRow label={formatMessage({ id: 'schoolProperty-House' })}>
            <PropertyTypeSelectMultiple
              borderOnHover
              allowArchived
              schoolId={schoolId}
              onClear={onClearHouse}
              optionsTitle={formatMessage({ id: 'schoolProperty-House-plural' })}
              userRole={SchoolUserRole.Student}
              propertyType={SchoolPropertyType.House}
              selectedIds={filters.house || []}
              onSelectId={onSelectHouseId}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.Subject) && (
          <FilterRow label={formatMessage({ id: 'groups-GroupSubject' })}>
            <SubjectSelectMultiple
              borderOnHover
              schoolId={schoolId}
              onClear={onClearSubject}
              optionsTitle={formatMessage({ id: 'groups-GroupSubject-plural' })}
              selectedIds={filters.subject || []}
              onSelectSubjectId={onSelectSubject}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.Gender) && (
          <FilterRow label={formatMessage({ id: 'peopleDetail-Gender' })}>
            <GenderSelectMultiple
              borderOnHover
              onClear={onClearGender}
              selectedGenders={filters.gender || []}
              onSelectGender={onSelectGender}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.Nationality) && (
          <FilterRow label={formatMessage({ id: 'peopleDetail-Nationality' })}>
            <NationalitySelectMultiple
              borderOnHover
              onClear={onClearNationality}
              selectedNationalities={filters.nationality || []}
              onSelectNationality={onSelectNationality}
            />
          </FilterRow>
        )}
        {isGroupViewer && filtersAvailable.includes(FilterKeys.Group) && (
          <>
            <FilterRow
              label={`${
                excludeGroups ? `${formatMessage({ id: 'filter-NotIn' })} ` : ''
              }${formatMessage({ id: 'groups-Group' })}`}
            >
              <GroupSelectMultiple
                borderOnHover
                onClear={onClearGroup}
                schoolId={schoolId}
                filters={groupFilters}
                selectedIds={filters.group || []}
                onSelectGroupId={onSelectGroupId}
              />
            </FilterRow>
            {initialExcludeGroups !== undefined && (
              <FilterRow label="">
                <Stack flexDirection="row" alignItems="center" gap={1} pt={0.5} pl={2}>
                  <Typography color={!excludeGroups ? 'text.primary' : 'text.secondary'}>
                    {formatMessage({ id: 'filter-include' })}
                  </Typography>
                  <Switch
                    checked={excludeGroups}
                    onChange={() => setExcludeGroups(!excludeGroups)}
                  />
                  <Typography color={excludeGroups ? 'text.primary' : 'text.secondary'}>
                    {formatMessage({ id: 'filter-exclude' })}
                  </Typography>
                </Stack>
              </FilterRow>
            )}
          </>
        )}
      </ModalContent>
      <ModalFooter active>
        <Button variant="outlined" onClick={handleClear}>
          {formatMessage({ id: 'filter-ResetToDefault' })}
        </Button>
        <Button onClick={handleApply}>{formatMessage({ id: 'filter-Apply' })}</Button>
      </ModalFooter>
    </Dialog>
  );
};

const FilterRow: FC<PropsWithChildren<{ label: string }>> = ({ label, children }) => (
  <Stack flexDirection="row" alignItems="center" gap={2}>
    <Box sx={{ flex: '1 1 15%' }}>
      <Typography color="common.grey2">{label}</Typography>
    </Box>
    <Box sx={{ flex: '1 1 85%' }}>{children}</Box>
  </Stack>
);
